import React, { FC, useContext } from 'react'
import styled from 'styled-components'
import { Icon, ScrollHWrapper, Fonts } from 'components/base-ui'
import { SpicyLevelEnum } from 'enums'
import { weight } from 'components/base-ui/Fonts'
import media from 'styles/media'
import { AppContext } from 'contexts/AppProvider'
import { getDataTags } from 'helpers/tags'
import { useTranslation } from 'react-i18next'
import { ProductQuery, BuffetProductDetailQuery } from 'graphQL/dinein/generate/operations'
import { Tag, TagCategory } from 'types/ProductTag'

interface ProductDetailsProps {
  product: ProductQuery['product'] | BuffetProductDetailQuery['product']
}

const ProductDetails: FC<ProductDetailsProps> = ({ product }) => {
  const { isThaiLang } = useContext(AppContext)
  const { t } = useTranslation()

  const { tasteDescriptionTh, tasteDescriptionEn, ingredientList, nutrition } = product
  let ingredientsStr = ''
  let isHaveNutrition = false
  const tasteDescription = isThaiLang ? tasteDescriptionTh : tasteDescriptionEn
  let complementaryTag: any[] = []
  let restrictionTag: any[] = []
  let benefitTag: any[] = []

  ingredientList.forEach((ingredient, i) => {
    if (isThaiLang) {
      ingredientsStr += i + 1 !== ingredientList.length ? `${ingredient.nameTh}, ` : ingredient.nameTh
    } else {
      ingredientsStr += i + 1 !== ingredientList.length ? `${ingredient.nameEn}, ` : ingredient.nameEn
    }
  })

  const nutritionList = ['calories', 'carbG', 'fatG', 'fiberG', 'proteinG', 'sodiumMg']
  for (let key in nutrition) {
    if (nutrition[key] !== null && nutritionList.indexOf(key) !== -1) {
      isHaveNutrition = true
    }
  }

  let productDescription = ''
  if (product) {
    productDescription = isThaiLang ? product.descriptionTh : product.descriptionEn
  }

  const newTags: Tag[] = getDataTags(product.tags)
  complementaryTag = newTags.filter((tag) => tag.categories.indexOf(TagCategory.Complementary) !== -1)
  restrictionTag = newTags.filter((tag) => tag.categories.indexOf(TagCategory.Restriction) !== -1)
  benefitTag = newTags.filter(
    (tag) => tag.categories.indexOf(TagCategory.Benefit) !== -1 && tag.nameEn !== 'Paleo' && tag.nameEn !== 'Keto'
  )

  return (
    <>
      {((product.maxSpicyLevel !== SpicyLevelEnum.NotSpicy && product.maxSpicyLevel !== null) || tasteDescription) && (
        <SectionProductDetail>
          <ProductDetailContent>
            <ProductDetailTitle>{t('productDetailTaste')}</ProductDetailTitle>
            <ProductDetailText>{tasteDescription}</ProductDetailText>
            {product.maxSpicyLevel !== SpicyLevelEnum.NotSpicy && (
              <PepperTag level={product.maxSpicyLevel}>
                <PepperIconWrapper>
                  {product.maxSpicyLevel === SpicyLevelEnum.Mild && <PepperIcon name="pepper" />}
                  {product.maxSpicyLevel === SpicyLevelEnum.Medium && <PepperIcon name="pepper-2" />}
                  {product.maxSpicyLevel === SpicyLevelEnum.Hot && <PepperIcon name="pepper-3" />}
                </PepperIconWrapper>
                {product.maxSpicyLevel === SpicyLevelEnum.Mild ? t('papperLevelMild') : ''}
                {product.maxSpicyLevel === SpicyLevelEnum.Medium ? t('papperLevelMedium') : ''}
                {product.maxSpicyLevel === SpicyLevelEnum.Hot ? t('papperLevelHot') : ''}
              </PepperTag>
            )}
          </ProductDetailContent>
        </SectionProductDetail>
      )}

      {productDescription && (
        <SectionProductDetail>
          <ProductDetailContent>
            <ProductDetailTitle>{t('productDetailDetail')}</ProductDetailTitle>
            <ProductDetailText>{productDescription}</ProductDetailText>
          </ProductDetailContent>
        </SectionProductDetail>
      )}

      {product.ingredientList.length > 0 && (
        <SectionProductDetail>
          <ProductDetailContent>
            <ProductDetailTitle>{t('productDetailIngredient')}</ProductDetailTitle>
            <ProductDetailText>{ingredientsStr}</ProductDetailText>
          </ProductDetailContent>
        </SectionProductDetail>
      )}

      {isHaveNutrition && (
        <SectionProductDetail>
          <ProductDetailContent>
            <ProductDetailTitle>{t('productDetailNutrition')}</ProductDetailTitle>
            {product.nutrition.servingsPerItem && (
              <DetailTextServing>
                {product.nutrition.servingsPerItem} {t('servingsPerItem')}
              </DetailTextServing>
            )}
            <IngredientLabels>
              <ScrollHWrapper>
                {product.nutrition.calories !== null && (
                  <IngredientLabel>
                    <IngredientLabelValue>{product.nutrition.calories}</IngredientLabelValue>
                    <IngredientLabelName>Calories</IngredientLabelName>
                    <IngredientLabelUnit>kcal</IngredientLabelUnit>
                  </IngredientLabel>
                )}

                {product.nutrition.carbG !== null && (
                  <IngredientLabel>
                    <IngredientLabelValue type="carbG">{product.nutrition.carbG}</IngredientLabelValue>
                    <IngredientLabelName>Carbs</IngredientLabelName>
                    <IngredientLabelUnit>g</IngredientLabelUnit>
                  </IngredientLabel>
                )}

                {product.nutrition.proteinG !== null && (
                  <IngredientLabel>
                    <IngredientLabelValue type="proteinG">{product.nutrition.proteinG}</IngredientLabelValue>
                    <IngredientLabelName>Protein</IngredientLabelName>
                    <IngredientLabelUnit>g</IngredientLabelUnit>
                  </IngredientLabel>
                )}

                {product.nutrition.fatG !== null && (
                  <IngredientLabel>
                    <IngredientLabelValue type="fatG">{product.nutrition.fatG}</IngredientLabelValue>
                    <IngredientLabelName>Fat</IngredientLabelName>
                    <IngredientLabelUnit>g</IngredientLabelUnit>
                  </IngredientLabel>
                )}

                {product.nutrition.fiberG !== null && (
                  <IngredientLabel>
                    <IngredientLabelValue type="fiberG">{product.nutrition.fiberG}</IngredientLabelValue>
                    <IngredientLabelName>Fiber</IngredientLabelName>
                    <IngredientLabelUnit>g</IngredientLabelUnit>
                  </IngredientLabel>
                )}
              </ScrollHWrapper>
            </IngredientLabels>
          </ProductDetailContent>
        </SectionProductDetail>
      )}

      {(complementaryTag.length > 0 || benefitTag.length > 0) && (
        <SectionProductDetail>
          <ProductDetailContent>
            <ProductDetailTitle>{t('productDetailWhatGood')}</ProductDetailTitle>
            <GoodIcons>
              <ScrollHWrapper>
                {benefitTag.map((tag, i) => {
                  return (
                    <GoodIcon key={i}>
                      <GoodCircle color={tag.color} bgColor={tag.bgColor}>
                        <Icon name={tag.icon} />
                      </GoodCircle>
                      <GoodText>{isThaiLang ? tag.nameTh : tag.nameEn}</GoodText>
                    </GoodIcon>
                  )
                })}
              </ScrollHWrapper>
            </GoodIcons>
            <CheckList>
              {complementaryTag.map((tag, i) => {
                return (
                  <CheckBox key={i}>
                    <CheckCircle>
                      <Icon name="correct" />
                    </CheckCircle>
                    {isThaiLang ? tag.nameTh : tag.nameTh}
                  </CheckBox>
                )
              })}
            </CheckList>
          </ProductDetailContent>
        </SectionProductDetail>
      )}

      {restrictionTag.length > 0 && (
        <SectionProductDetail>
          <ProductDetailContent>
            <ProductDetailTitle>{t('productDetailAllergy')} </ProductDetailTitle>
            <LimitationTags>
              {restrictionTag.map((tag, i) => {
                return <LimitationTag key={i}>{isThaiLang ? tag.nameTh : tag.nameEn}</LimitationTag>
              })}
            </LimitationTags>
          </ProductDetailContent>
        </SectionProductDetail>
      )}
    </>
  )
}

export default ProductDetails

const SectionProductDetail = styled.div`
  margin-bottom: 25px;
  display: flex;
`

const ProductDetailContent = styled.div`
  flex: 1;
`

const ProductDetailTitle = styled(Fonts.Medium)`
  color: ${(props) => props.theme.colors.gray1000};
`

const ProductDetailText = styled(Fonts.Base)`
  color: black;
`

const LimitationTag = styled.div`
  display: flex;
  align-items: center;
  border-radius: 8px;
  background-color: ${(props) => props.theme.colors.gray100};
  height: 28px;
  padding: 0 10px;
  font-size: 14px;
  font-weight: ${weight.medium};
  color: ${(props) => props.theme.colors.gray700};
`

const LimitationTags = styled.div`
  margin-bottom: -8px;
  display: flex;
  flex-wrap: wrap;
  padding-top: 12px;

  ${LimitationTag} {
    margin-right: 8px;
    margin-bottom: 8px;
  }
  ${LimitationTag}:last-child {
    margin-right: 0;
  }
`

const GoodIcons = styled.div`
  margin-top: 6px;
  width: calc(100vw + 12px) !important;
  margin-left: -51px;
  white-space: nowrap;

  @media (${media.screenMdMin}) {
    width: 414px !important;
  }
`

const GoodIcon = styled.div`
  display: inline-block;
  margin-right: 20px;
  width: 78px;
  vertical-align: top;

  &:first-child {
    margin-left: 55px;
  }
`

const GoodCircle = styled.div<{ bgColor?: string; disabled?: boolean; colorBorder?: string }>`
  margin-right: auto;
  margin-bottom: 7px;
  margin-left: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 64px;
  height: 64px;
  background-color: ${(props) => (props.bgColor ? props.bgColor : props.theme.colors.green100)};
  border-radius: 50%;

  i {
    font-size: 30px;
    color: ${(props) => (props.color ? props.color : props.theme.colors.primary)};
  }

  ${(props) =>
    props.disabled
      ? `
    &::after {
      content: '';
      display: block;
      width: 100%;
      height: 1px;
      background-color: ${props.colorBorder ? props.colorBorder : props.theme.colors.green200};
      position: absolute;
      top: 50%;
      left: 0;
      transform: rotate(-45deg);
    }
    `
      : ''}
`

const GoodText = styled.div`
  font-size: 14px;
  font-weight: ${weight.medium};
  color: ${(props) => props.theme.colors.gray700};
  text-align: center;
  white-space: normal;
`

const IngredientLabels = styled.div`
  margin-top: 6px;
  width: calc(100vw) !important;
  margin-left: -52px;
  white-space: nowrap;

  @media (${media.screenMdMin}) {
    width: 414px !important;
  }
`

const IngredientLabel = styled.div`
  display: inline-block;
  margin-right: 20px;
  width: 56px;
  height: 97px;
  border-radius: 28px;
  background-color: ${(props) => props.theme.colors.yellow100};

  &:first-child {
    margin-left: 55px;
  }
`

const IngredientLabelValue = styled.div<{ type?: string }>`
  margin-top: 8px;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: white;
  font-weight: ${weight.semiBold};
  color: ${(props) => props.theme.colors.orange400};

  ${(props) =>
    props.type === 'carbG'
      ? `
    color: ${props.theme.colors.brown500};
  `
      : ''}

  ${(props) =>
    props.type === 'proteinG'
      ? `
    color: ${props.theme.colors.lightBlue};
  `
      : ''}

  ${(props) =>
    props.type === 'fatG'
      ? `
    color: ${props.theme.colors.yellow500};
  `
      : ''}

  ${(props) =>
    props.type === 'fiberG'
      ? `
    color: ${props.theme.colors.primary};
  `
      : ''}
`

const IngredientLabelName = styled.div`
  font-size: 12px;
  font-weight: ${weight.medium};
  color: ${(props) => props.theme.colors.gray700};
  text-align: center;
`

const IngredientLabelUnit = styled.div`
  font-size: 10px;
  color: ${(props) => props.theme.colors.gray300};
  text-align: center;
`

const PepperTag = styled.div<{ level: SpicyLevelEnum }>`
  margin-top: 8px;
  display: inline-flex;
  align-items: center;
  height: 24px;
  border-radius: 12px;
  padding-right: 10px;
  padding-left: 8px;
  font-size: 14px;
  font-weight: ${weight.medium};

  ${(props) =>
    props.level === SpicyLevelEnum.Mild
      ? `
    background-color: ${props.theme.colors.yellow100};
    background-color: ${props.theme.colors.gray100};
    color: ${props.theme.colors.yellow};
    color: ${props.theme.colors.gray700};

    i {
      color: ${props.theme.colors.yellow};
    }
    `
      : ''}

  ${(props) =>
    props.level === SpicyLevelEnum.Medium
      ? `
    background-color: ${props.theme.colors.orange100};
    background-color: ${props.theme.colors.gray100};
    color: ${props.theme.colors.orange};
    color: ${props.theme.colors.gray700};

    i {
      color: ${props.theme.colors.orange};
    }
    `
      : ''}

  ${(props) =>
    props.level === SpicyLevelEnum.Hot
      ? `
    background-color: #FFE5E9;
    background-color: ${props.theme.colors.gray100};
    color: ${props.theme.colors.red};
    color: ${props.theme.colors.gray700};

    i {
      color: ${props.theme.colors.red};
    }
    `
      : ''}
`

const PepperIconWrapper = styled.div`
  margin-right: 5px;
  display: flex;
  justify-content: center;
  width: 14px;
`

const PepperIcon = styled((props) => <Icon {...props} />)`
  font-size: 12px;
`

const CheckCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: ${(props) => props.theme.colors.primary};

  i {
    position: relative;
    top: 1px;
    font-size: 8px;
    color: white;
  }
`

const CheckList = styled.div`
  margin-top: 25px;
  margin-bottom: -3px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
`

const CheckBox = styled.div`
  margin-bottom: 13px;
  display: flex;
  align-items: center;
  width: 50%;
  font-size: 14px;
  color: ${(props) => props.theme.colors.gray600};
`

const DetailTextServing = styled.span`
  display: block;
  margin-top: -1px;
  margin-bottom: 12px;
  font-size: 12px;
  color: ${(props) => props.theme.colors.gray700};
`
